import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/native';
import { TouchableOpacity, Image } from 'react-native';
import { useMutation } from '@apollo/client';
import { white, green, lightGreen } from '@/shared/styles/colors';
import { Stylist } from '@/shared/types/generated';
import { formatCurrency, formatNameWithLastInitial } from '@/shared/utils';
import { BaseText, H4 } from '@/shared/components/StyledText';
import { TOGGLE_STYLIST_FAVORITE } from '@/shared/mutations';
import { GET_STYLISTS, GET_STYLIST } from '@/shared/queries/stylistSchema';
import SentryService from '@/services/sentry/Sentry.service';
import { createResizedPhotoUrl, HALF_WIDTH } from '@/shared/utils/photoUtils';
import { useEventConfiguration } from '@/shared/hooks/useEventConfiguration';

interface StylistCardProps {
  item: Stylist;
  onPress: () => void;
  estimatedItemSize: number;
  estimatedItemDimension: number;
}

const StylistCard: React.FC<StylistCardProps> = ({
  item,
  onPress,
  estimatedItemSize,
  estimatedItemDimension,
}) => {
  const [isFavoritedOptimistic, setIsFavoritedOptimistic] = useState(
    item.isFavorited,
  );

  const { inPersonEventSessions } = useEventConfiguration();

  // Sync optimistic state when item changes
  useEffect(() => {
    setIsFavoritedOptimistic(item.isFavorited);
  }, [item.isFavorited]);

  const [toggleStylistFavorite] = useMutation(TOGGLE_STYLIST_FAVORITE, {
    update: (cache, { data }) => {
      if (data?.toggleStylistFavorite !== undefined) {
        const newFavoriteStatus = data.toggleStylistFavorite;

        // Update GET_STYLISTS cache
        try {
          const existingStylists = cache.readQuery<{ stylists: Stylist[] }>({
            query: GET_STYLISTS,
          });
          if (existingStylists?.stylists) {
            const updatedStylists = existingStylists.stylists.map(
              (stylist: Stylist) =>
                stylist.id === item.id
                  ? { ...stylist, isFavorited: newFavoriteStatus }
                  : stylist,
            );

            cache.writeQuery({
              query: GET_STYLISTS,
              data: { stylists: updatedStylists },
            });
          }
        } catch (error) {
          // Cache query doesn't exist yet, which is fine
        }

        // Update individual GET_STYLIST cache if it exists
        try {
          const existingStylist = cache.readQuery<{ stylist: Stylist }>({
            query: GET_STYLIST,
            variables: { input: { id: item.id } },
          });
          if (existingStylist?.stylist) {
            cache.writeQuery({
              query: GET_STYLIST,
              variables: { input: { id: item.id } },
              data: {
                stylist: {
                  ...existingStylist.stylist,
                  isFavorited: newFavoriteStatus,
                },
              },
            });
          }
        } catch (error) {
          // Individual stylist cache doesn't exist, which is fine
        }
      }
    },
    onError: () => {
      // Revert optimistic update on error
      setIsFavoritedOptimistic(item.isFavorited);
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleToggleFavorite = async (event: any) => {
    event.stopPropagation(); // Prevent card press when heart is tapped

    const newFavoriteState = !isFavoritedOptimistic;

    setIsFavoritedOptimistic(newFavoriteState);

    try {
      await toggleStylistFavorite({
        variables: {
          input: {
            stylistId: item.id,
          },
        },
      });
    } catch (error) {
      SentryService.captureError('Error toggling favorite', error);
    }
  };

  const getPricingDisplay = () => {
    if (item.customHourlyRate) {
      return `${formatCurrency(item.customHourlyRate)}/hour`;
    } else if (item.pricing?.hourlyRate) {
      return `${formatCurrency(item.pricing.hourlyRate)}/hour`;
    }
    return 'XXX/hour';
  };

  const resizedProfilePicUrl = useMemo(() => {
    return item.user?.profilePicUrl
      ? createResizedPhotoUrl(item.user.profilePicUrl, HALF_WIDTH)
      : '';
  }, [item.user?.profilePicUrl]);

  const inPersonAvailability = item.computedInPersonAvailability;

  const shouldShowBanner =
    (item.isEventAttendee && inPersonEventSessions) || inPersonAvailability;

  return (
    <CardWrapper key={item.id} estimatedItemSize={estimatedItemSize}>
      <CardContainer
        onPress={onPress}
        estimatedItemDimension={estimatedItemDimension}
      >
        <HeartIconContainer onPress={handleToggleFavorite}>
          <HeartIcon
            source={
              isFavoritedOptimistic
                ? require('@/assets/images/FilledHeart.png')
                : require('@/assets/images/WhiteFavorite.png')
            }
          />
        </HeartIconContainer>
        <StylistImage source={{ uri: resizedProfilePicUrl }} />
        {shouldShowBanner && (
          <EventAttendeeBanner>
            <BannerText>AVAILABLE IN PERSON</BannerText>
          </EventAttendeeBanner>
        )}
      </CardContainer>
      <StylistName>{formatNameWithLastInitial(item.user)}</StylistName>
      <StylistPrice>{getPricingDisplay()}</StylistPrice>
    </CardWrapper>
  );
};

export default StylistCard;

const CardWrapper = styled.View(
  ({ estimatedItemSize }: { estimatedItemSize: number }) => ({
    marginBottom: 16,
    height: estimatedItemSize,
  }),
);

const CardContainer = styled(TouchableOpacity)(
  ({ estimatedItemDimension }: { estimatedItemDimension: number }) => ({
    backgroundColor: white,
    borderRadius: 12,
    height: estimatedItemDimension,
    width: estimatedItemDimension,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 16,
  }),
);

const HeartIconContainer = styled.TouchableOpacity({
  position: 'absolute',
  top: 2,
  left: 2,
  zIndex: 2,
  borderRadius: 12,
  padding: 8,
});

const HeartIcon = styled(Image)({
  width: 24,
  height: 24,
  resizeMode: 'contain',
});

const StylistImage = styled(Image)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  resizeMode: 'cover',
});

const StylistName = styled(BaseText)({
  fontSize: 20,
  color: green,
});

const StylistPrice = styled(BaseText)({
  fontSize: 12,
  color: lightGreen,
  letterSpacing: 1,
});

const EventAttendeeBanner = styled.View({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(0, 41, 27, 0.5)',
  padding: 8,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
});

const BannerText = styled(H4)({
  fontSize: 9,
  color: white,
  fontWeight: 'bold',
  textAlign: 'center',
  lineHeight: 11,
});
